import { CircularProgress } from '@mui/joy';

import { useState } from 'react';

import { TabGroup } from '@/components/Tabs/Tabs';
import { TabLabel, TabList, TabPanel, TabPanels, Text } from '@/design-system';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentOrganizationBilling } from '@/hooks/useSwr';

import { OrganizationUsage } from '../OrganizationUsage';
import { BusinessInformation } from '../components/BusinessInformation';
import { ConfigureUsagePanel } from '../components/ConfigureUsagePanel';
import { OrganizationDashboard } from '../components/OrganizationDashboard';
import { PaymentMethod } from '../components/PaymentMethod';
import { PricingLink } from '../components/PricingLink';
import { SetUpBilling } from '../components/SetUpBilling';
import { SubscribeToPlanModal } from '../components/SubscribeToPlanModal';
import { PaymentCards } from './PaymentCards';
import { StartupApplyBanner } from './StartupApplyBanner';
import { StatusModal } from './StatusModals';
import { useShowApprovalPlanCard } from './hooks/useShowApprovalPlanCard';
import { useBillingPlanStore } from './store';
import { paymentPlanCardTiers } from './types';

export const PlansAndBillingPageComponentV2 = () => {
  const { data: currentOrganization, isLoading: currentOrganizationIsLoading } =
    useCurrentOrganizationBilling();
  const { is_personal: isPersonal } = currentOrganization ?? {};

  const { authorize } = usePermissions('organization');

  const [error, setError] = useState<string | null>(null);

  const showTTL = useInstanceFlag(InstanceFlags.show_ttl_ui);

  const {
    tier: currentTier,
    isLegacy,
    isNoPlan,
    isLoading: isTierLoading,
  } = useCurrentTier();

  const targetPlanTier = useBillingPlanStore((s) => s.targetPlanTier);

  const showStartupV0 = useShowApprovalPlanCard({
    planType: 'startup_v0',
    checkApproval: false,
  });
  const showStartup = useShowApprovalPlanCard({ planType: 'startup' });

  if (!currentOrganization && !currentOrganizationIsLoading) {
    return <div>Organization not found</div>;
  }

  const legacySetupBillingMainPage = currentOrganization && (
    <div className="mx-auto flex flex-col gap-5">
      <SetUpBilling />
    </div>
  );
  let mainPageElement = currentOrganization && (
    <div className="mt-3 flex flex-col items-stretch gap-10">
      <div className="mb-10 flex w-fit flex-col items-stretch gap-5">
        {isNoPlan && (
          <div className="rounded-lg bg-tertiary px-6 py-5">
            <div className="flex items-center gap-2">
              <Text variant="xs" weight="semibold">
                Heads up!
              </Text>
              <Text variant="xs" weight="normal">
                You’re currently in a team organization. Upgrade to Plus to use
                this organization.
              </Text>
            </div>
          </div>
        )}

        <PaymentCards />
        <div className="flex flex-col gap-3">
          {!showStartupV0 && !showStartup && <StartupApplyBanner />}
          <PricingLink />
        </div>
      </div>
      <PaymentMethod />
      {!isNoPlan && <BusinessInformation />}
    </div>
  );
  if (!authorize('organization:manage')) {
    mainPageElement = (
      <div className="rounded-lg bg-secondary px-5 py-5 pl-8">
        You do not have the manage organization permission to view billing
        information.
      </div>
    );
  }

  const header = isPersonal && (
    <Text variant="h2" className="mb-4 text-xl font-semibold">
      Billing and usage
    </Text>
  );

  if (currentOrganizationIsLoading || isTierLoading) {
    return <CircularProgress />;
  }

  return (
    <TabGroup setTabMethod="merge">
      <div className="flex items-end gap-9 border-b border-secondary">
        {header}
        {Object.keys(paymentPlanCardTiers).includes(currentTier || '') && (
          <TabList className="border-b-0">
            <TabLabel label="Plans and Pricing" />
            <TabLabel label="Usage graph" />
            {showTTL && <TabLabel label="Usage configuration" />}
            <TabLabel label="Credits" />
            <TabLabel label="Invoices" />
          </TabList>
        )}
      </div>
      {Object.keys(paymentPlanCardTiers).includes(currentTier || '') ? (
        <TabPanels className="flex flex-auto flex-col overflow-auto">
          <TabPanel className="p-4">{mainPageElement}</TabPanel>

          <TabPanel className="flex-auto p-4">
            <OrganizationUsage />
          </TabPanel>
          {showTTL && (
            <TabPanel className="flex-auto p-4">
              <ConfigureUsagePanel />
            </TabPanel>
          )}
          <TabPanel className="p-4">
            <div className="flex h-[700px]">
              <OrganizationDashboard type={'credits'} />
            </div>
          </TabPanel>
          <TabPanel className="p-4 px-0">
            <div className="flex h-[700px]">
              <OrganizationDashboard type={'invoices'} />
            </div>
          </TabPanel>
        </TabPanels>
      ) : isLegacy ? (
        <div className="flex flex-row gap-8 pt-5">
          {legacySetupBillingMainPage}
        </div>
      ) : (
        mainPageElement
      )}
      <SubscribeToPlanModal
        setError={setError}
        targetPlanTier={targetPlanTier ?? null}
      />
      {targetPlanTier && (
        <StatusModal tier={targetPlanTier} error={error} setError={setError} />
      )}
    </TabGroup>
  );
};
